/**
 * Generic selection helper that manages either a single selected item or a list of selected items.
 * Primarily used by controllers to keep reactive `selection.value` state.
 *
 * @template {Record<string, any>} T
 */
export default class Selection {
	/**
	 * @param {T[]} items
	 * @param {{ multiple?: boolean }=} options
	 */
	constructor(items, { multiple = false } = {}) {
		/** @type {T[]} */
		this._items = items;

		if (multiple) {
			/** @type {{ value: T[] }} */
			this._selection = { value: [] };
		} else {
			/** @type {{ value: T | null }} */
			this._selection = { value: null };
		}
		/** @type {boolean} */
		this._multiple = multiple;
	}

	/** @returns {boolean} */
	get multiple() {
		return this._multiple;
	}

	/** @returns {T[]} */
	get items() {
		return this._items;
	}

	/** @returns {{ value: (T[] | T | null) }} */
	get selection() {
		return this._selection;
	}

	/** @param {T} x */
	select(x) {
		if (this._multiple) {
			this._selection.value.push(x);
		} else {
			this._selection.value = x;
		}
	}

	/** @param {T & { id?: (string | number) }} item */
	deselect(item) {
		if (this._multiple) {
			this._selection.value = this._selection.value.filter(
				(i) => i.id !== item.id,
			);
		} else {
			this._selection.value = null;
		}
	}

	reset() {
		if (this._multiple) {
			this._selection.value = [];
		} else {
			this._selection.value = null;
		}
	}

	/**
	 * Adds all items where `item[name] == value` to the current selection.
	 *
	 * @param {string} name
	 * @param {string} value
	 */
	addSelectionByProperty(name, value) {
		this.items.filter((item) => {
			if (item[name] == value) {
				this.select(item);
			}
		});
	}

	/**
	 * Selects items by property. If `multiple` is enabled, the selection is reset before adding matches.
	 *
	 * @param {string} name
	 * @param {string} value
	 */
	selectByProperty(name, value) {
		if (this.multiple) {
			this.reset();
		}
		this.addSelectionByProperty(name, value);
	}

	/** @param {T[]} items */
	setItems(items) {
		this._items = items;
	}
	removeItems() {
		this._items = [];
	}
}
